import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Clock,
  Calendar,
  Globe,
  Heart,
  Mail,
  Menu,
  X,
  Sparkles,
  Star,
  MessageSquare,
  Lightbulb,
  Scale,
  Briefcase,
  Tag,
  LayoutGrid,
  type LucideIcon,
} from 'lucide-react';
import { SignInButton, UserButton, useAuth } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { Button, LanguageModal } from '@/components/ui';
import { SUPPORTED_LANGS, type BlogLang } from '@/lib/blogLangs';
import { getPostSummariesByLang, type BlogPostSummary } from '@/lib/blogIndex';
import { useState, useEffect, useMemo, useRef } from 'react';

const categoryColors: Record<string, string> = {
  cyan: 'bg-cyan-500/10 text-cyan-600',
  emerald: 'bg-emerald-500/10 text-emerald-600',
  amber: 'bg-amber-500/10 text-amber-600',
  rose: 'bg-rose-500/10 text-rose-600',
  indigo: 'bg-indigo-500/10 text-indigo-600',
  blue: 'bg-blue-500/10 text-blue-600',
};

interface BlogPageProps {
  /** When the route is /{lang}/blog, App.tsx forces the language so the page
   *  is deterministic for crawlers regardless of i18n state. */
  langOverride?: BlogLang;
}


export function BlogPage({ langOverride }: BlogPageProps = {}) {
  const { i18n } = useTranslation();
  const { isSignedIn } = useAuth();
  // i18n only knows hr/en — for sl/de/it we still pin lang from the route.
  const lang: BlogLang = langOverride ?? (
    SUPPORTED_LANGS.includes(i18n.language as BlogLang) ? (i18n.language as BlogLang) : 'hr'
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  // Load posts for the active language only — empty array means we haven't
  // generated translations into that language yet (UI shows empty state).
  const posts = getPostSummariesByLang(lang);

  useEffect(() => {
    // Only sync i18n for hr/en (it doesn't have sl/de/it bundles loaded).
    if (langOverride && (langOverride === 'hr' || langOverride === 'en') && i18n.language !== langOverride) {
      i18n.changeLanguage(langOverride);
    }
  }, [langOverride, i18n]);

  useEffect(() => {
    const h = () => setHeaderScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const [langOpen, setLangOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const prefersReducedMotion = useReducedMotion();
  const heroRef = useRef<HTMLElement>(null);
  const { scrollY } = useScroll();
  const heroParallax = useTransform(scrollY, [0, 400], [0, -60]);

  // Featured = newest post by date. Only used in hero when filter is "all"
  // — once the user filters by category, every post is equally relevant so
  // promoting one would feel arbitrary.
  const featuredPost = useMemo(() => {
    if (posts.length === 0) return null;
    return [...posts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  }, [posts]);

  // Build unique category list from posts — preserves first-seen ordering so the
  // pills appear in the same order as the first card of each category.
  const categories = useMemo(() => {
    const seen = new Map<string, string>();
    posts.forEach((p) => {
      if (!seen.has(p.category)) seen.set(p.category, p.categoryColor);
    });
    return Array.from(seen.entries()).map(([category, color]) => ({ category, color }));
  }, [posts]);

  // When showing "all", we strip the featured post from the grid so it doesn't
  // appear twice. Filtered views show everything in that category, including
  // the featured one (because the hero featured card hides in those views).
  const filteredPosts = useMemo(() => {
    if (activeCategory === 'all') {
      return featuredPost ? posts.filter((p) => p.slug !== featuredPost.slug) : posts;
    }
    return posts.filter((p) => p.category === activeCategory);
  }, [posts, activeCategory, featuredPost]);

  // Selecting a language in the modal navigates to the sibling URL — the blog
  // is split into per-language routes (/hr/blog, /sl/blog, ...), so we have
  // to actually change the URL rather than just flipping i18n. We fall back
  // to /hr/blog for any code outside the supported set just to be safe.
  const handleLangSelect = (target: string) => {
    if (!SUPPORTED_LANGS.includes(target as BlogLang)) target = 'hr';
    if (target === 'hr' || target === 'en') i18n.changeLanguage(target);
    window.location.href = `/${target}/blog/`;
  };

  // Trailing slash matches what Cloudflare Pages serves (pretty URLs 308
  // /path → /path/ before _redirects can intervene). Keeps canonical,
  // sitemap and JSON-LD in agreement with the URL Googlebot lands on.
  const blogBaseUrl = `https://bookiapp.com/${lang}/blog/`;

  const t = {
    hr: {
      title: 'Savjeti za iznajmljivače',
      subtitle: 'Praktični vodiči, predlošci i iskustva za profesionalnije iznajmljivanje',
      readMore: 'Čitaj više',
      minRead: 'min čitanja',
      signIn: 'Prijava',
      dashboard: 'Dashboard',
      pricing: 'Cijene',
      about: 'O nama',
      contact: 'Kontakt',
      cta: 'Započni sada',
      footerDesc: 'Sve o boravku — u jednoj aplikaciji za vaše goste.',
      legal: 'Pravno', privacy: 'Privatnost', terms: 'Uvjeti korištenja', cookies: 'Kolačići',
      company: 'Tvrtka',
      madeIn: 'Napravljeno s', inCroatia: 'u Hrvatskoj', rights: 'Sva prava pridržana.',
      empty: 'Uskoro novi članci.',
      navBlog: 'Savjeti',
      all: 'Sve',
      featured: 'Izdvojeno',
      latest: 'Najnovije',
    },
    en: {
      title: 'Tips for vacation rental hosts',
      subtitle: 'Practical guides, templates and real-world experience for better hosting',
      readMore: 'Read more',
      minRead: 'min read',
      signIn: 'Sign In',
      dashboard: 'Dashboard',
      pricing: 'Pricing',
      about: 'About',
      contact: 'Contact',
      cta: 'Get started',
      footerDesc: 'Guest Experience platform for modern hosts.',
      legal: 'Legal', privacy: 'Privacy', terms: 'Terms of Service', cookies: 'Cookies',
      company: 'Company',
      madeIn: 'Made with', inCroatia: 'in Croatia', rights: 'All rights reserved.',
      empty: 'New articles coming soon.',
      navBlog: 'Tips',
      all: 'All',
      featured: 'Featured',
      latest: 'Latest',
    },
    sl: {
      title: 'Nasveti za lastnike apartmajev',
      subtitle: 'Praktični vodniki, predloge in izkušnje za boljše oddajanje na Hrvaškem',
      readMore: 'Preberi več',
      minRead: 'min branja',
      signIn: 'Prijava',
      dashboard: 'Nadzorna plošča',
      pricing: 'Cene',
      about: 'O nas',
      contact: 'Kontakt',
      cta: 'Začni zdaj',
      footerDesc: 'Vse o bivanju — v eni aplikaciji za vaše goste.',
      legal: 'Pravno', privacy: 'Zasebnost', terms: 'Pogoji uporabe', cookies: 'Piškotki',
      company: 'Podjetje',
      madeIn: 'Narejeno s', inCroatia: 'na Hrvaškem', rights: 'Vse pravice pridržane.',
      empty: 'Nove objave prihajajo kmalu.',
      navBlog: 'Nasveti',
      all: 'Vse',
      featured: 'Izpostavljeno',
      latest: 'Najnovejše',
    },
    de: {
      title: 'Tipps für Vermieter',
      subtitle: 'Praktische Anleitungen, Vorlagen und Erfahrungen für die Vermietung in Kroatien',
      readMore: 'Weiterlesen',
      minRead: 'Min. Lesezeit',
      signIn: 'Anmelden',
      dashboard: 'Dashboard',
      pricing: 'Preise',
      about: 'Über uns',
      contact: 'Kontakt',
      cta: 'Jetzt starten',
      footerDesc: 'Alles zum Aufenthalt — in einer App für Ihre Gäste.',
      legal: 'Rechtliches', privacy: 'Datenschutz', terms: 'AGB', cookies: 'Cookies',
      company: 'Unternehmen',
      madeIn: 'Gemacht mit', inCroatia: 'in Kroatien', rights: 'Alle Rechte vorbehalten.',
      empty: 'Neue Artikel folgen bald.',
      navBlog: 'Tipps',
      all: 'Alle',
      featured: 'Hervorgehoben',
      latest: 'Neueste',
    },
    it: {
      title: 'Consigli per host',
      subtitle: 'Guide pratiche, modelli ed esperienze per affittare meglio in Croazia',
      readMore: 'Leggi di più',
      minRead: 'min di lettura',
      signIn: 'Accedi',
      dashboard: 'Dashboard',
      pricing: 'Prezzi',
      about: 'Chi siamo',
      contact: 'Contatti',
      cta: 'Inizia ora',
      footerDesc: "Tutto sul soggiorno — in un'unica app per i tuoi ospiti.",
      legal: 'Legale', privacy: 'Privacy', terms: 'Termini', cookies: 'Cookie',
      company: 'Azienda',
      madeIn: 'Realizzato con', inCroatia: 'in Croazia', rights: 'Tutti i diritti riservati.',
      empty: 'Nuovi articoli in arrivo.',
      navBlog: 'Consigli',
      all: 'Tutti',
      featured: 'In evidenza',
      latest: 'Ultimi',
    },
  }[lang];

  // ── Schema graph: Organization + WebSite + Blog ──
  // Multiple schemas linked via @id form a knowledge graph that AI engines
  // (ChatGPT, Perplexity, Gemini) prefer over isolated schemas.
  const orgId = 'https://bookiapp.com/#organization';
  const websiteId = 'https://bookiapp.com/#website';

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': orgId,
    name: 'BookiApp',
    url: 'https://bookiapp.com',
    logo: {
      '@type': 'ImageObject',
      url: 'https://bookiapp.com/favicon-original.png',
      width: 512,
      height: 512,
    },
    description: {
      hr: 'Aplikacija za goste smještaja — sve informacije o boravku na jednom mjestu, za male iznajmljivače.',
      en: 'Guest experience app for short-term rental hosts — all stay info in one place.',
      sl: 'Aplikacija za goste nastanitev — vse informacije o bivanju na enem mestu, za majhne najemodajalce.',
      de: 'App für Gäste von Unterkünften — alle Aufenthaltsinfos an einem Ort, für kleine Vermieter.',
      it: "App per gli ospiti di alloggi — tutte le informazioni sul soggiorno in un unico posto, per piccoli host.",
    }[lang],
    foundingDate: '2025',
    areaServed: { '@type': 'Country', name: 'Hrvatska' },
    knowsLanguage: ['hr', 'en', 'sl', 'de', 'it'],
    sameAs: [
      // TODO: add real social URLs when accounts are live
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': websiteId,
    url: 'https://bookiapp.com',
    name: 'BookiApp',
    inLanguage: ['hr-HR', 'en-US'],
    publisher: { '@id': orgId },
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `https://bookiapp.com/${lang}/blog?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  };

  // BCP-47 locale tags per supported language.
  const localeMap: Record<BlogLang, string> = {
    hr: 'hr-HR', en: 'en-US', sl: 'sl-SI', de: 'de-DE', it: 'it-IT',
  };

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: t.title,
    description: t.subtitle,
    url: blogBaseUrl,
    inLanguage: localeMap[lang],
    publisher: { '@id': orgId },
    blogPost: posts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      description: p.description,
      datePublished: p.date,
      dateModified: p.lastReviewed || p.date,
      author: { '@type': 'Person', name: p.author.name },
      image: p.coverImage.startsWith('http') ? p.coverImage : `https://bookiapp.com${p.coverImage}`,
      url: `${blogBaseUrl}${p.slug}/`,
    })),
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Helmet>
        <html lang={lang} />
        <title>{t.title} | BookiApp</title>
        <meta name="description" content={t.subtitle} />
        <link rel="canonical" href={blogBaseUrl} />
        <meta property="og:locale" content={localeMap[lang].replace('-', '_')} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${t.title} | BookiApp`} />
        <meta property="og:description" content={t.subtitle} />
        <meta property="og:url" content={blogBaseUrl} />
        <meta property="og:site_name" content="BookiApp" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${t.title} | BookiApp`} />
        <meta name="twitter:description" content={t.subtitle} />

        {/* hreflang — every supported locale points at its own URL.
            x-default falls back to HR (our primary launch market). */}
        {SUPPORTED_LANGS.map((l) => (
          <link key={l} rel="alternate" hrefLang={l} href={`https://bookiapp.com/${l}/blog/`} />
        ))}
        <link rel="alternate" hrefLang="x-default" href="https://bookiapp.com/hr/blog/" />

        {/* JSON-LD — Organization + WebSite + Blog graph for AI knowledge mapping */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerScrolled ? 'bg-[hsl(var(--background))]/70 backdrop-blur-2xl border-b border-[hsl(var(--border))]/50 shadow-lg shadow-black/[0.03]' : 'bg-transparent'}`}>
        <div className="container mx-auto flex items-center justify-between px-4 py-3 md:py-4">
          <Link to="/" className="flex items-center group shrink-0">
            <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto md:h-10 group-hover:scale-105 transition-transform duration-300" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {[{ to: '/pricing/', label: t.pricing }, { to: `/${lang}/blog/`, label: t.navBlog }, { to: '/about/', label: t.about }, { to: '/contact/', label: t.contact }].map(l => (
              <Link key={l.to} to={l.to} className={`text-sm font-medium transition-colors relative group ${l.to.endsWith('/blog/') ? 'text-[hsl(var(--foreground))]' : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'}`}>
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 group-hover:w-full transition-all duration-300" />
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLangOpen(true)}
              className="px-2 md:px-3 hover:bg-cyan-500/10"
              aria-haspopup="dialog"
              aria-expanded={langOpen}
            >
              <Globe className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">{lang.toUpperCase()}</span>
            </Button>
            {!isSignedIn && (
              <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
                <Button variant="ghost" size="sm" className="hidden sm:flex hover:bg-cyan-500/10">{t.signIn}</Button>
              </SignInButton>
            )}
            <Link to={isSignedIn ? '/dashboard' : '/pricing'} className="hidden sm:block">
              <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0 text-xs md:text-sm shadow-lg shadow-cyan-500/20">{isSignedIn ? t.dashboard : t.cta}</Button>
            </Link>
            {isSignedIn && (
              <UserButton
                afterSignOutUrl="/"
                appearance={{ elements: { avatarBox: 'h-9 w-9 rounded-xl ring-2 ring-transparent hover:ring-cyan-200 transition-all duration-200' } }}
              />
            )}
            <Button variant="ghost" size="sm" className="md:hidden px-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-[hsl(var(--background))]/95 backdrop-blur-xl">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-3">
              {[{ to: '/pricing/', label: t.pricing }, { to: `/${lang}/blog/`, label: t.navBlog }, { to: '/about/', label: t.about }, { to: '/contact/', label: t.contact }].map(l => (
                <Link key={l.to} to={l.to} className="text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>{l.label}</Link>
              ))}
              {isSignedIn ? (
                <Link to="/dashboard" className="text-sm font-medium py-2 text-cyan-600" onClick={() => setMobileMenuOpen(false)}>{t.dashboard}</Link>
              ) : (
                <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
                  <button className="text-sm font-medium py-2 text-left" onClick={() => setMobileMenuOpen(false)}>{t.signIn}</button>
                </SignInButton>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Hero — animated mesh gradient + parallax title */}
      <section ref={heroRef} className="pt-24 pb-8 md:pt-32 md:pb-12 relative overflow-hidden">
        {/* Animated mesh gradient blobs — stronger so they actually read */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            aria-hidden
            className="absolute -top-[30%] -left-[15%] w-[75%] h-[75%] bg-gradient-to-br from-cyan-400/35 via-blue-400/25 to-transparent rounded-full blur-[120px]"
            animate={prefersReducedMotion ? undefined : {
              x: [0, 40, 0],
              y: [0, 30, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            aria-hidden
            className="absolute top-[5%] -right-[15%] w-[65%] h-[65%] bg-gradient-to-bl from-indigo-400/30 via-purple-400/20 to-transparent rounded-full blur-[120px]"
            animate={prefersReducedMotion ? undefined : {
              x: [0, -30, 0],
              y: [0, 40, 0],
              scale: [1, 1.15, 1],
            }}
            transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            aria-hidden
            className="absolute top-[20%] left-[35%] w-[35%] h-[45%] bg-gradient-to-tr from-pink-400/15 via-rose-400/10 to-transparent rounded-full blur-[100px]"
            animate={prefersReducedMotion ? undefined : {
              x: [0, 20, -20, 0],
              y: [0, -20, 20, 0],
              scale: [1, 1.1, 0.95, 1],
            }}
            transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
          />
          {/* Grid overlay for subtle texture */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.025] dark:opacity-[0.04]"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
              WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            }}
          />
        </div>
        <motion.div
          style={prefersReducedMotion ? undefined : { y: heroParallax }}
          className="container mx-auto px-4 relative text-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-3 md:mb-4 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-800 dark:text-cyan-200 text-xs font-semibold backdrop-blur-sm shadow-sm shadow-cyan-500/10"
          >
            <Sparkles className="h-3.5 w-3.5" />
            BookiApp Blog
          </motion.div>
          {/* leading-[1.15] + pb-2 prevents `bg-clip-text` from cropping
              descenders (j/g/y) — Safari/Chrome clip them when the line box is
              tight against the baseline. */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: 'easeOut' }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.15] pb-2 mb-2 bg-clip-text text-transparent bg-gradient-to-r from-cyan-500 to-blue-600"
          >
            {t.title}
          </motion.h1>
          {/* Editorial accent line — short cyan→transparent rule under the title */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.25, ease: 'easeOut' }}
            aria-hidden
            className="mx-auto mb-5 h-[3px] w-20 rounded-full bg-gradient-to-r from-transparent via-cyan-500 to-transparent"
            style={{ transformOrigin: 'center' }}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
            className="text-base sm:text-lg md:text-xl text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto"
          >
            {t.subtitle}
          </motion.p>
        </motion.div>
      </section>

      {/* Featured post — newest article, hero-sized. Only on "all" view. */}
      {featuredPost && activeCategory === 'all' && (
        <section className="pb-10 md:pb-16">
          <div className="container mx-auto px-4">
            <FeaturedPost
              post={featuredPost}
              lang={lang}
              readMoreLabel={t.readMore}
              minReadLabel={t.minRead}
              featuredLabel={t.featured}
              locale={localeMap[lang]}
              categoryColors={categoryColors}
              reducedMotion={!!prefersReducedMotion}
            />
          </div>
        </section>
      )}

      {/* Category filter — glass card with icons */}
      {posts.length > 0 && categories.length > 1 && (
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="pb-6 md:pb-10 sticky top-[60px] md:top-[68px] z-30 pt-2"
        >
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div
                role="tablist"
                aria-label="Blog categories"
                className="relative flex items-center gap-1 overflow-x-auto scrollbar-hide rounded-2xl bg-[hsl(var(--card))]/70 backdrop-blur-xl border border-[hsl(var(--border))]/60 shadow-lg shadow-black/[0.04] p-1.5"
              >
                <FilterPill
                  icon={LayoutGrid}
                  label={t.all}
                  count={posts.length}
                  active={activeCategory === 'all'}
                  onClick={() => setActiveCategory('all')}
                />
                {categories.map(({ category, color }) => (
                  <FilterPill
                    key={category}
                    icon={getCategoryIcon(category)}
                    label={category}
                    count={posts.filter((p) => p.category === category).length}
                    active={activeCategory === category}
                    color={color}
                    onClick={() => setActiveCategory(category)}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.section>
      )}

      {/* Posts grid — scroll-reveal stagger + tilt/glow cards */}
      <section className="pt-6 md:pt-10 pb-16 md:pb-24">
        <div className="container mx-auto px-4">
          {posts.length === 0 ? (
            <div className="text-center text-[hsl(var(--muted-foreground))] py-20">
              <p>{t.empty}</p>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
                >
                  {filteredPosts.map((post, idx) => (
                    <BlogCard
                      key={post.slug}
                      post={post}
                      idx={idx}
                      lang={lang}
                      readMoreLabel={t.readMore}
                      minReadLabel={t.minRead}
                      locale={localeMap[lang]}
                      categoryColors={categoryColors}
                      reducedMotion={!!prefersReducedMotion}
                    />
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-10 md:py-14 bg-[hsl(var(--card))]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-8 md:mb-10">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center mb-4"><img src="/favicon-original.png" alt="BookiApp" className="h-7 w-auto md:h-8" /></Link>
              <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{t.footerDesc}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm">{t.legal}</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.privacy}</Link></li>
                <li><Link to="/terms" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.terms}</Link></li>
                <li><Link to="/cookies" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.cookies}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm">{t.company}</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/about" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.about}</Link></li>
                <li><Link to="/pricing" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.pricing}</Link></li>
                <li><Link to={`/${lang}/blog/`} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.navBlog}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm">{t.contact}</h4>
              <ul className="space-y-3 text-sm"><li className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]"><Mail className="h-4 w-4" /><a href="mailto:admin@bookiapp.com" className="hover:text-[hsl(var(--foreground))] transition-colors">admin@bookiapp.com</a></li></ul>
            </div>
          </div>
          <div className="pt-8 border-t flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">&copy; 2026 BookiApp. {t.rights}</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-1">{t.madeIn} <Heart className="h-4 w-4 text-red-500 fill-red-500" /> {t.inCroatia}</p>
          </div>
        </div>
      </footer>

      <LanguageModal
        open={langOpen}
        onClose={() => setLangOpen(false)}
        scope="marketing"
        currentLang={lang}
        onSelect={handleLangSelect}
      />
    </div>
  );
}

/* ─── FeaturedPost ───
 * Magazine-style hero card for the newest article. Cover image on one side,
 * editorial typography on the other, full-bleed glow halo behind.
 * Stacks to image-on-top on mobile.
 */
function FeaturedPost({
  post,
  lang,
  readMoreLabel,
  minReadLabel,
  featuredLabel,
  locale,
  categoryColors,
  reducedMotion,
}: {
  post: BlogPostSummary;
  lang: BlogLang;
  readMoreLabel: string;
  minReadLabel: string;
  featuredLabel: string;
  locale: string;
  categoryColors: Record<string, string>;
  reducedMotion: boolean;
}) {
  const [hover, setHover] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.35, ease: 'easeOut' }}
      className="max-w-6xl mx-auto"
    >
      <Link
        to={`/${lang}/blog/${post.slug}/`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="block group"
      >
        <article className="relative grid md:grid-cols-2 gap-0 overflow-hidden rounded-3xl border border-[hsl(var(--border))]/60 bg-[hsl(var(--card))] shadow-xl shadow-black/[0.05] hover:shadow-2xl hover:shadow-cyan-500/10 transition-shadow duration-500">
          {/* Glow halo */}
          <div
            aria-hidden
            className="absolute -inset-px rounded-3xl bg-gradient-to-br from-cyan-500/20 via-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10 blur-2xl"
          />

          {/* Cover image */}
          <div className="relative h-64 md:h-auto md:min-h-[420px] overflow-hidden">
            <motion.img
              src={post.coverImage}
              alt={post.coverImageAlt}
              loading="eager"
              className="absolute inset-0 w-full h-full object-cover"
              animate={reducedMotion ? undefined : { scale: hover ? 1.05 : 1 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            />
            {/* Top-left featured badge */}
            <div className="absolute top-4 left-4 z-10">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/90 dark:bg-black/70 backdrop-blur-md text-cyan-700 dark:text-cyan-300 ring-1 ring-cyan-500/30 shadow-lg">
                <Sparkles className="h-3.5 w-3.5" />
                {featuredLabel}
              </span>
            </div>
            {/* Bottom gradient overlay — visible on mobile for chip legibility */}
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/50 to-transparent pointer-events-none md:hidden" />
          </div>

          {/* Editorial side */}
          <div className="relative p-6 md:p-10 lg:p-12 flex flex-col justify-center">
            <div className="flex flex-wrap items-center gap-3 mb-4 md:mb-5">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  categoryColors[post.categoryColor] || 'bg-cyan-500/10 text-cyan-600'
                }`}
              >
                {post.category}
              </span>
              <div className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]">
                <Clock className="h-3 w-3" />
                <span>
                  {post.readTime} {minReadLabel}
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]">
                <Calendar className="h-3 w-3" />
                <span>
                  {new Date(post.date).toLocaleDateString(locale, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.6rem] font-extrabold tracking-tight leading-[1.1] mb-4 md:mb-5 group-hover:text-cyan-600 transition-colors">
              {post.title}
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed mb-6 md:mb-8 line-clamp-3">
              {post.description}
            </p>
            <div className="flex items-center gap-2 text-sm md:text-base font-semibold text-cyan-600">
              <span>{readMoreLabel}</span>
              <motion.span
                animate={reducedMotion ? undefined : { x: hover ? 6 : 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-cyan-500/10 group-hover:bg-cyan-500 group-hover:text-white transition-colors"
              >
                <ArrowRight className="h-4 w-4" />
              </motion.span>
            </div>
          </div>
        </article>
      </Link>
    </motion.div>
  );
}

/* ─── Category icon mapping ───
 * Maps localized category names to lucide icons. The match is *substring*-based
 * and case-insensitive so a single rule works across HR/EN/SL/DE/IT
 * (e.g. "Recenzije" + "Reviews" + "Bewertungen" all match the "recen|review|bewert"
 *  pattern). Anything unrecognized falls back to a generic Tag.
 */
function getCategoryIcon(category: string): LucideIcon {
  const c = category.toLowerCase();
  if (/recen|review|bewert|recens/.test(c)) return Star;
  if (/komun|comm|kommun|comuni|sporoč/.test(c)) return MessageSquare;
  if (/savjet|tip|consig|nasvet|tipps/.test(c)) return Lightbulb;
  if (/propis|legal|recht|legg|predpis/.test(c)) return Scale;
  if (/poslov|busin|gesch|affari|posel/.test(c)) return Briefcase;
  return Tag;
}

/* ─── FilterPill ───
 * Glass-segment pill. Active state uses framer-motion's layoutId so the
 * gradient background "morphs" from one segment to the next instead of
 * crossfading — the signature transition for this kind of nav.
 */
function FilterPill({
  icon: Icon,
  label,
  count,
  active,
  color,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  count: number;
  active: boolean;
  color?: string;
  onClick: () => void;
}) {
  // Map the category color token to a matching gradient for the active state.
  // Falls back to the brand cyan→blue gradient for the "All" segment.
  const gradientMap: Record<string, string> = {
    cyan: 'from-cyan-500 to-blue-600',
    emerald: 'from-emerald-500 to-teal-600',
    amber: 'from-amber-500 to-orange-600',
    rose: 'from-rose-500 to-pink-600',
    indigo: 'from-indigo-500 to-violet-600',
    blue: 'from-blue-500 to-cyan-600',
  };
  const gradient = color ? gradientMap[color] || gradientMap.cyan : 'from-cyan-500 to-blue-600';
  const shadowMap: Record<string, string> = {
    cyan: 'shadow-cyan-500/30',
    emerald: 'shadow-emerald-500/30',
    amber: 'shadow-amber-500/30',
    rose: 'shadow-rose-500/30',
    indigo: 'shadow-indigo-500/30',
    blue: 'shadow-blue-500/30',
  };
  const shadow = color ? shadowMap[color] || shadowMap.cyan : 'shadow-cyan-500/30';

  return (
    <button
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={`relative shrink-0 px-3 md:px-4 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50 ${
        active
          ? 'text-white'
          : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
      }`}
    >
      {active && (
        <motion.span
          layoutId="active-pill"
          aria-hidden
          className={`absolute inset-0 rounded-xl bg-gradient-to-r ${gradient} shadow-lg ${shadow}`}
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        />
      )}
      <span className="relative flex items-center gap-1.5 md:gap-2">
        <Icon className="h-3.5 w-3.5 md:h-4 md:w-4" />
        <span>{label}</span>
        <span
          className={`ml-0.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-semibold rounded-full ${
            active
              ? 'bg-white/25 text-white'
              : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'
          }`}
        >
          {count}
        </span>
      </span>
    </button>
  );
}

/* ─── BlogCard ───
 * Per-card scroll-reveal + tilt-on-hover + glow + cover parallax.
 * useReducedMotion is honoured — for users with the OS pref we keep the card
 * static but still allow the underlying link to work normally.
 */
function BlogCard({
  post,
  idx,
  lang,
  readMoreLabel,
  minReadLabel,
  locale,
  categoryColors,
  reducedMotion,
}: {
  post: BlogPostSummary;
  idx: number;
  lang: BlogLang;
  readMoreLabel: string;
  minReadLabel: string;
  locale: string;
  categoryColors: Record<string, string>;
  reducedMotion: boolean;
}) {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const [hover, setHover] = useState(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (reducedMotion) return;
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    // Tilt range ±6deg — strong enough to feel premium but never disorienting.
    setTilt({ rx: (0.5 - py) * 6, ry: (px - 0.5) * 6 });
  };

  const handleMouseLeave = () => {
    setHover(false);
    setTilt({ rx: 0, ry: 0 });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: Math.min(idx * 0.06, 0.4), ease: 'easeOut' }}
      style={{ perspective: 1000 }}
    >
      <Link
        ref={cardRef}
        to={`/${lang}/blog/${post.slug}/`}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={handleMouseLeave}
        className="block group"
        style={{
          transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
          transition: 'transform 0.2s ease-out',
          transformStyle: 'preserve-3d',
        }}
      >
        <article className="relative bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))]/50 overflow-hidden h-full flex flex-col transition-colors duration-300 group-hover:border-cyan-500/40">
          {/* Glow gradient — revealed on hover */}
          <div
            aria-hidden
            className={`absolute -inset-px rounded-2xl bg-gradient-to-br from-cyan-500/20 via-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl`}
          />
          {/* Cover Image with parallax zoom */}
          <div className="relative h-48 overflow-hidden">
            <motion.img
              src={post.coverImage}
              alt={post.coverImageAlt}
              loading="lazy"
              className="w-full h-full object-cover"
              animate={reducedMotion ? undefined : { scale: hover ? 1.08 : 1 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
            {/* Bottom fade for legibility of overlay chips on dark covers */}
            <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
            {/* Category chip floats on image */}
            <div className="absolute top-3 left-3 flex items-center gap-2">
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-md ${
                  categoryColors[post.categoryColor] || 'bg-cyan-500/10 text-cyan-600'
                } ring-1 ring-white/40 shadow-lg`}
              >
                {post.category}
              </span>
            </div>
            <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-black/40 backdrop-blur-md text-white">
              <Clock className="h-3 w-3" />
              <span>
                {post.readTime} {minReadLabel}
              </span>
            </div>
          </div>
          <div className="p-6 md:p-7 flex flex-col flex-1">
            <h2 className="text-lg md:text-xl font-bold mb-3 group-hover:text-cyan-600 transition-colors line-clamp-2">
              {post.title}
            </h2>
            <p className="text-sm md:text-base text-[hsl(var(--muted-foreground))] leading-relaxed mb-5 flex-1 line-clamp-3">
              {post.description}
            </p>
            <div className="flex items-center justify-between pt-4 border-t border-[hsl(var(--border))]/30">
              <div className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]">
                <Calendar className="h-3 w-3" />
                <span>
                  {new Date(post.date).toLocaleDateString(locale, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              </div>
              <span className="text-sm font-medium text-cyan-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                {readMoreLabel}
                <motion.span
                  animate={reducedMotion ? undefined : { x: hover ? 4 : 0 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <ArrowRight className="h-4 w-4" />
                </motion.span>
              </span>
            </div>
          </div>
        </article>
      </Link>
    </motion.div>
  );
}

export default BlogPage;
